import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import VBox from 'components/common/VBox';
import FocusableButton from 'components/common/FocusableButton';
import React from 'react';
import Recoil from 'recoil';
import * as AppStore from 'stores/AppStore';
import useDoctorGuideList from 'hooks/communication/useDoctorGuideList';
import usePaging from 'hooks/tables/usePaging';
import PagingIndicator from 'components/common/paging/PagingIndicator';
import defaultRenderPrevButtons from 'components/common/paging/defaultRenderPrevButtons';
import defaultRenderNextButtons from 'components/common/paging/defaultRenderNextButtons';
import HBox from 'components/common/HBox';
import * as Luxon from 'luxon';
import useDoctorGuideDelete from 'hooks/communication/useDoctorGuideDelete';
import useMessageBox from 'hooks/app/useMessageBox';

const PAGE_ROW_CNT = 16;
export default function ListArea(props) {
  const {
    category,
    current_time,
    onCurrentTime,
    guide,
    reload,
    setReload,
    ...rest
  } = props;

  const [total, setTotal] = React.useState(0);
  const [list, setList] = Recoil.useRecoilState(AppStore.assets.guide_list);

  const doctor_info = Recoil.useRecoilValue(AppStore.assets.doctor_info);
  const patient_no = Recoil.useRecoilValue(AppStore.assets.patient_no);
  const {
    dr_id,
  } = doctor_info || {};

  const {
    page,
    onPage,
    npage,
    onNPage,
    maxPage,
    onMaxPage
  } = usePaging({
    resetCondition: [],
    defaultMaxPage: 6,
  });

  const {
    requestDoctorGuideList,
  } = useDoctorGuideList();

  const {
    onDoctorGuideDelete,
  } = useDoctorGuideDelete();

  React.useEffect(() => {
    requestDoctorGuideList({
      dr_id,
      patient_no,
      page_row_cnt: PAGE_ROW_CNT,
      page_no: npage,
    }, (success, data, parameters) => {
      if (success) {
        setList(() => data.list);
        onPage(data.page_no);
        onMaxPage(Math.max(1, data.max_page_no));
        setTotal(() => data.total_row_cnt);
        onCurrentTime((t) => t ?? data.list[0]?.guide_time)();
      } else {
        setTotal(() => 0);
        setList(() => []);
        onMaxPage(1);
        onPage(1);
        onNPage(1);
      }
    })
  }, [dr_id, onMaxPage, onPage, npage, patient_no, guide, reload, requestDoctorGuideList, setList, onNPage, onCurrentTime]);

  const {
    open,
    update,
    close,
  } = useMessageBox(category);

  const onDeleted = React.useCallback((success, data, parameters) => {
    if (success) {
      open({
        id: 'delete',
        title: T('Dr.Guide 삭제'),
        contents: (
          <div>
            {T('Dr.Guide 삭제를 성공했습니다.')}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('delete'),
        },
      });
      setReload(r => r + 1);
    } else {
      open({
        id: 'delete',
        title: T('Dr.Guide 삭제 실패'),
        contents: (
          <div>
            {data.message}
          </div>
        ),
        buttons: [ex('primary', {
          autoFocus: true,
          caption: T('확인'),
          event: 'onClose',
        })],
        events: {
          onClose: () => close('delete'),
        },
      });
    }
  }, [close, open, setReload]);

  return (
    <VBox {...ex('ListArea', rest)} full between>
      <table>
        <thead>
          <tr>
            <th>
              <div>
                {T('No.')}
              </div>
            </th>
            <th>
              <div>
                {T('발행일')}
              </div>
            </th>
            <th>
              <div>
                {T('활동')}
              </div>
            </th>
            <th>
              <div>
                {T('식사')}
              </div>
            </th>
            <th>
              <div>
                {T('인슐린')}
              </div>
            </th>
            <th>
              <div>
                {T('작성일')}
              </div>
            </th>
            <th>
              <div>
                {T('비고')}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, index) => {
            const {
              guide_time,
              send_time,
              send_yn,
              active_guide,
              meal_guide,
              insulin_guide,
            } = item;
            return (
              <tr
                key={guide_time}
                {...ex({ selected: current_time === guide_time }, {
                  onClick: onCurrentTime(() => guide_time),
                })}
              >
                <td>
                  <div>
                    {index + (page - 1) * PAGE_ROW_CNT + 1}
                  </div>
                </td>
                <td>
                  <div>
                    {send_time && Luxon.DateTime.fromFormat(send_time, 'yyyyMMddHHmmss').toFormat('yy.MM.dd')}
                  </div>
                </td>
                <td>
                  <div>
                    {active_guide ? '○' : ''}
                  </div>
                </td>
                <td>
                  <div>
                    {meal_guide ? '○' : ''}
                  </div>
                </td>
                <td>
                  <div>
                    {insulin_guide ? '○' : ''}
                  </div>
                </td>
                <td>
                  <div>
                    {guide_time && Luxon.DateTime.fromFormat(guide_time, 'yyyyMMddHHmmss').toFormat('yy.MM.dd')}
                  </div>
                </td>
                <td>
                  <div>
                    {send_yn === 'Y' && T('발행완료')}
                    {send_yn !== 'Y' && (
                      <FocusableButton
                        {...ex('DrGuideDeleteButton')}
                        {...{
                          onClick: onDoctorGuideDelete({ dr_id, patient_no, guide_time }, onDeleted),
                        }}
                      >
                        {T('삭제')}
                      </FocusableButton>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <HBox center>
        <PagingIndicator
          {...{
            indicatorCount: 5,
            maxPage,
            page,
            onNPage,
            renderPrevButtons: defaultRenderPrevButtons,
            renderNextButtons: defaultRenderNextButtons,
          }}
        />
      </HBox>
    </VBox >
  );
}
